/**
 * Overview Tab Section Components
 * Extracted from OverviewTab for better maintainability
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, DollarSign, Users, Clock, AlertTriangle,
  Calendar, CheckCircle2, Activity, Target, BarChart3,
  ChevronRight, Award, ListTodo, Sparkles, ArrowUpRight,
  HeartHandshake, LineChart, Package, MessageSquare, Eye,
  Settings, ExternalLink, Building2, UserCircle, Shield, Link2
} from 'lucide-react';
import { toast } from '@/utils/safeToast';
import { INDUSTRY_CONFIG } from '@/config/dashboardConfig';
import { getIndustryData, INDUSTRY_COMPETITORS, INDUSTRY_SUPPLIERS } from '@/config/industryDemoData';
import { getIndustryBenchmarks, DATA_SOURCES } from '@/config/industryBenchmarks';
import { WinCard, OpportunityCard } from '../../insights/InsightEngineComponents';
import BusinessConcernsCard from '../BusinessConcernsCard';
import { EnhancedTaskItem } from '../EnhancedTaskPanel';
import { DataSourceBadge, BenchmarkBadge, ConnectedSourcesPanel } from '@/components/ui/DataSourceBadge';
import {
  CompetitorIntelligencePanel,
  RevenueForecastingWidget,
  PLSnapshotWidget,
  StaffPerformanceLeaderboard,
  CustomerSentimentTracker,
  WeatherImpactWidget,
  SupplierPerformanceTracker,
  GoalTrackerWidget,
} from '../widgets';
import PLBenchmarkWidget from '../widgets/PLBenchmarkWidget';
import { KPICard, RecommendationCard } from './DashboardCards';
import { BenchmarkAlerts } from '../BenchmarkAlerts';

/**
 * Section Header Component - Reusable header for dashboard sections
 * Now includes optional data provenance indicator
 */
export const SectionHeader = ({ icon: Icon, iconColor, title, subtitle, dataSources }) => (
  <div className="flex items-center justify-between">
    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
      {title}
      {subtitle && <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— {subtitle}</span>}
    </h3>
    {dataSources && dataSources.length > 0 && (
      <div className="flex items-center gap-1">
        {dataSources.map(src => (
          <DataSourceBadge key={src} sourceId={src} size="xs" />
        ))}
      </div>
    )}
  </div>
);

/**
 * Recent Wins Section
 */
export const WinsSection = ({ isEnabled, unifiedWins, industry, connectedSources = [], isDemoAccount = false }) => {
  if (!isEnabled) return null;

  const industryConfig = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.hospitality;
  const defaultWins = [
    {
      title: `Strong ${industryConfig?.kpis[0] || 'Performance'}`,
      description: `Your ${industryConfig?.name?.toLowerCase() || 'business'} outperformed benchmarks by 15% this period.`,
      metric: "+15%",
      metricLabel: "vs industry avg",
      improvement: "3 consecutive weeks",
      source: "xero",
      benchmarkSource: "ibisworld"
    },
    {
      title: "Customer Retention Up",
      description: "Repeat customer rate has increased significantly.",
      metric: "87%",
      metricLabel: "retention rate",
      improvement: "+5% vs last month",
      source: "square"
    }
  ];

  // Live accounts: ONLY real wins, and each win's source badges are filtered to
  // integrations that are ACTUALLY connected — we never fabricate a Xero/Square
  // badge. Canned demo wins render only for explicit demo accounts.
  const connected = new Set((connectedSources || []).map((s) => String(s).toLowerCase()));
  const realWins = (unifiedWins || []).map((w) => ({
    ...w,
    sources: (w.sources || [w.source]).filter(Boolean).filter((s) => connected.has(String(s).toLowerCase())),
  }));
  const wins = realWins.length > 0 ? realWins : (isDemoAccount ? defaultWins : []);

  if (wins.length === 0) {
    return (
      <div className="space-y-3" data-testid="wins-empty">
        <SectionHeader icon={Award} iconColor="text-emerald-500" title="Recent Wins" subtitle="Celebrate your achievements" />
        <Card className="glass-card">
          <CardContent className="p-6 text-center">
            <Award className="w-8 h-8 text-emerald-500/40 mx-auto mb-2" />
            <p className="text-sm text-slate-500 mb-3">No wins to celebrate yet — connect your accounting or POS source and Corvana will surface real, verified wins here (never placeholder numbers).</p>
            <button onClick={() => window.location.assign('/app/integrations')} className="text-sm text-primary hover:underline" data-testid="wins-connect-cta">Connect a source →</button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader icon={Award} iconColor="text-emerald-500" title="Recent Wins" subtitle="Celebrate your achievements" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {wins.slice(0, 2).map((win, idx) => (
          <WinCard
            key={`${win?.id ?? win?.name ?? win?.title ?? 'win'}-${idx}`}
            title={win.title}
            description={win.description}
            metric={win.metric}
            metricLabel={win.metricLabel}
            improvement={win.improvement}
            sources={win.sources || (win.source ? [win.source] : [])}
            timeframe={idx === 0 ? "this week" : "this month"}
            icon={idx === 1 ? HeartHandshake : Award}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Business Concerns Section
 */
export const ConcernsSection = ({ isEnabled, canSeeTeamView, industry, isDemoAccount = false }) => {
  if (!canSeeTeamView || !isEnabled) return null;
  
  return (
    <BusinessConcernsCard 
      className="w-full"
      maxItems={3}
      industry={industry}
      isDemo={isDemoAccount}
      onItemAction={(item) => {
        toast.info(`Opening ${item.item || item.type || item.area || 'item'} details...`);
      }}
    />
  );
};

/**
 * Benchmark Alerts Section - Shows metrics below industry averages
 */
export const BenchmarkAlertsSection = ({ isEnabled, canSeeTeamView, industry, unifiedMetrics }) => {
  const navigate = useNavigate();
  
  if (!canSeeTeamView || !isEnabled) return null;
  
  // Provenance-first (iter-552): NEVER seed benchmark comparisons with demo
  // numbers. Use real metrics only; render nothing until they exist.
  const grossProfit = unifiedMetrics?.profitMargin?.current ?? unifiedMetrics?.gross_margin;
  const labourCost = unifiedMetrics?.labourCost?.current ?? unifiedMetrics?.labour_cost;
  const netProfit = unifiedMetrics?.netProfit?.current ?? unifiedMetrics?.net_profit;
  if (grossProfit == null && labourCost == null && netProfit == null) return null;
  const businessMetrics = { grossProfit, labourCost, netProfit };
  
  return (
    <div className="space-y-3">
      <BenchmarkAlerts 
        businessMetrics={businessMetrics}
        industry={industry}
        onViewDetails={(metricKey) => navigate(`/app/analytics?metric=${metricKey}`)}
      />
    </div>
  );
};

/**
 * Opportunities Section - Industry-aware, location-aware
 */
export const OpportunitiesSection = ({ isEnabled, unifiedOpportunities, industry, currentLocation }) => {
  if (!isEnabled) return null;
  
  // Always use industry-specific opportunities — never fall back to API data from wrong context
  const { getIndustryOpportunities } = require('@/config/industryOpportunities');
  const opportunities = getIndustryOpportunities(industry || 'hospitality');
  
  return (
    <div className="space-y-3">
      <SectionHeader icon={Target} iconColor="text-amber-500" title="Opportunities" subtitle={currentLocation ? `${currentLocation.shortName} — actions to grow` : 'Actions to grow your business'} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {opportunities.slice(0, 3).map((opp, idx) => (
          <OpportunityCard
            key={`${opp?.id ?? opp?.name ?? opp?.title ?? 'opp'}-${idx}`}
            title={opp.title}
            description={opp.description}
            reasoning={opp.reasoning}
            impact={opp.impact}
            priority={opp.priority}
            suggestedActions={opp.suggestedActions || []}
            sources={opp.sources || [opp.source, 'google_analytics']}
            schedule={opp.schedule}
            likelihood={opp.likelihood}
            runway={opp.runway}
            bestChance={opp.bestChance}
            dataSource={opp.dataSource}
            benchmarkSource={opp.benchmarkSource}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Financial Intelligence Section
 */
export const FinancialSection = ({ canSeeTeamView, isRevenueForecastEnabled, isPLEnabled, unifiedMetrics, industry, dataSources = [] }) => {
  const navigate = useNavigate();
  // Only show accounting-source tags that are ACTUALLY connected (never fabricate Xero).
  const financialDataSources = (Array.isArray(dataSources) ? dataSources : []).filter(
    (s) => ['myob', 'xero', 'quickbooks', 'square'].includes(String(s).toLowerCase())
  );

  if (!canSeeTeamView || (!isRevenueForecastEnabled && !isPLEnabled)) return null;
  
  return (
    <div className="space-y-3">
      <SectionHeader icon={DollarSign} iconColor="text-emerald-500" title="Financial Intelligence" subtitle="Revenue & profitability" dataSources={financialDataSources} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isRevenueForecastEnabled && (
          <RevenueForecastingWidget 
            baseRevenue={unifiedMetrics?.revenue?.daily}
            trend="up"
            industry={industry}
            onViewDetails={() => navigate('/app/management')}
          />
        )}
        {isPLEnabled && (
          <PLBenchmarkWidget 
            industry={industry}
            onViewDetails={() => navigate('/app/management')}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Performance & Feedback Section
 */
export const PerformanceSection = ({ canSeeTeamView, isStaffEnabled, isSentimentEnabled, industry, onExpandCard, isDemo = false }) => {
  if (!canSeeTeamView || (!isStaffEnabled && !isSentimentEnabled)) return null;

  // Real (non-demo) accounts must NOT see fabricated staff/sentiment data.
  // These widgets generate illustrative data from the industry, so for live
  // accounts we show a clear empty state until real sources are connected.
  const EmptyCard = ({ icon: Icon, title, body, cta, onClick, testId }) => (
    <div
      className="rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06)] p-8 flex flex-col items-center text-center"
      data-testid={testId}
    >
      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-slate-400" />
      </div>
      <h4 className="text-base font-semibold text-slate-800">{title}</h4>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">{body}</p>
      {cta && (
        <button
          onClick={onClick}
          className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {cta}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <SectionHeader icon={Users} iconColor="text-blue-500" title="Performance & Feedback" subtitle="Team & customer insights" />
      {/* Stacked vertically (full-width) so the rich cards never get squeezed
          into narrow diagonal/skinny columns where content overflows. */}
      <div className="grid grid-cols-1 gap-4">
        {isStaffEnabled && (
          isDemo ? (
            <div
              className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
              onClick={() => onExpandCard('staff-performance')}
              data-testid="staff-performance-card"
            >
              <StaffPerformanceLeaderboard
                industry={industry}
                onViewDetails={() => onExpandCard('staff-performance')}
                onViewStaff={(staff) => toast.info(`Viewing ${staff.name}'s profile`)}
              />
            </div>
          ) : (
            <EmptyCard
              icon={Users}
              title="No team performance data yet"
              body="Add staff or connect a workforce integration (e.g. Employment Hero, Deputy) to see performance rankings and recognition here."
              cta="Add your team →"
              onClick={() => window.location.assign('/app/team')}
              testId="staff-performance-empty"
            />
          )
        )}
        {isSentimentEnabled && (
          isDemo ? (
            <div
              className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
              onClick={() => onExpandCard('customer-sentiment')}
              data-testid="customer-sentiment-card"
            >
              <CustomerSentimentTracker
                industry={industry}
                onViewDetails={() => onExpandCard('customer-sentiment')}
                onRespondToReview={(review) => toast.info(`Responding to ${review.author}'s review`)}
              />
            </div>
          ) : (
            <EmptyCard
              icon={MessageSquare}
              title="No customer sentiment data yet"
              body="Connect a review source (Google Business Profile) to track ratings, sentiment trends and respond to reviews here."
              cta="Connect reviews →"
              onClick={() => window.location.assign('/app/integrations')}
              testId="customer-sentiment-empty"
            />
          )
        )}
      </div>
    </div>
  );
};

/**
 * Market & Operations Section
 */
export const MarketSection = ({ canSeeTeamView, isCompetitorEnabled, isSupplierEnabled, industry, onExpandCard, isDemo = false }) => {
  if (!canSeeTeamView || (!isCompetitorEnabled && !isSupplierEnabled)) return null;

  // Real accounts NEVER see placeholder data — competitor/supplier intel only
  // populates from real market scans + connected integrations. Demo accounts
  // keep illustrative data for sales walkthroughs.
  const competitorData = isDemo ? getIndustryData(INDUSTRY_COMPETITORS, industry) : null;
  const supplierData = isDemo ? getIndustryData(INDUSTRY_SUPPLIERS, industry) : null;

  const EmptyCard = ({ icon: Icon, title, body, cta, onClick, testId }) => (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 flex flex-col items-center text-center" data-testid={testId}>
      <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
        <Icon className="w-5 h-5 text-slate-400" />
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-xs text-slate-500 mt-1 max-w-md">{body}</p>
      {cta && (
        <button onClick={onClick} className="mt-3 text-xs font-medium text-primary hover:underline">{cta}</button>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <SectionHeader icon={BarChart3} iconColor="text-violet-500" title="Market & Operations" subtitle="Competitive landscape" dataSources={['ibisworld']} />
      <div className="grid grid-cols-1 gap-4">
        {isCompetitorEnabled && (
          isDemo ? (
            <div 
              className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
              onClick={() => onExpandCard('competitor-intelligence')}
              data-testid="competitor-intelligence-card"
            >
              <CompetitorIntelligencePanel 
                industry={industry}
                data={competitorData}
                onViewDetails={() => onExpandCard('competitor-intelligence')}
              />
            </div>
          ) : (
            <EmptyCard
              icon={Eye}
              title="No competitor data yet"
              body="Competitor pricing, ratings and market share appear here once we've scanned your market and connected your sources — no placeholder numbers."
              cta="Connect a source →"
              onClick={() => window.location.assign('/app/integrations')}
              testId="competitor-intelligence-empty"
            />
          )
        )}
        {isSupplierEnabled && (
          isDemo ? (
            <div 
              className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
              onClick={() => onExpandCard('supplier-performance')}
              data-testid="supplier-performance-card"
            >
              <SupplierPerformanceTracker 
                industry={industry}
                data={supplierData}
                onViewDetails={() => onExpandCard('supplier-performance')}
                onContactSupplier={(supplier) => toast.info(`Contacting ${supplier.name}`)}
              />
            </div>
          ) : (
            <EmptyCard
              icon={Package}
              title="No supplier data yet"
              body="Supplier on-time %, quality scores and spend appear here from your accounting integration (vendor bills) once connected — no placeholder numbers."
              cta="Connect accounting →"
              onClick={() => window.location.assign('/app/integrations')}
              testId="supplier-performance-empty"
            />
          )
        )}
      </div>
    </div>
  );
};

/**
 * Planning & Context Section
 */
export const PlanningSection = ({ canSeeTeamView, isWeatherEnabled, isGoalEnabled, industry, onExpandCard }) => {
  if (!canSeeTeamView || (!isWeatherEnabled && !isGoalEnabled)) return null;
  
  return (
    <div className="space-y-3">
      <SectionHeader icon={Calendar} iconColor="text-[#007AFF]" title="Planning & Context" subtitle="External factors & goals" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isWeatherEnabled && (
          <div 
            className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
            onClick={() => onExpandCard('weather-impact')}
          >
            <WeatherImpactWidget 
              industry={industry}
              onViewDetails={() => onExpandCard('weather-impact')}
            />
          </div>
        )}
        {isGoalEnabled && (
          <div 
            className="cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-2xl transition-all"
            onClick={() => onExpandCard('goal-tracker')}
          >
            <GoalTrackerWidget 
              industry={industry}
              onViewDetails={() => onExpandCard('goal-tracker')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * KPI Grid Section
 */
export const KPISection = ({ kpis, industry, dataSources = [] }) => {
  const navigate = useNavigate();
  const benchmarks = getIndustryBenchmarks(industry);
  const hasSources = Array.isArray(dataSources) && dataSources.length > 0;
  
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Key Performance Indicators
            </CardTitle>
            <CardDescription className="text-xs flex items-center gap-2 mt-1">
              Benchmarked against {benchmarks.industry} industry standards
              <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                <Shield className="w-2.5 h-2.5 mr-0.5 text-emerald-500" />
                Verified Sources
              </Badge>
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/management')}>
            Full Analytics <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {/* Connected Data Sources — reflects ACTUAL connections only. */}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
          <span className="text-[10px] text-slate-500">Data from:</span>
          {hasSources ? (
            <div className="flex gap-1">
              {dataSources.map(sourceId => (
                <DataSourceBadge key={sourceId} sourceId={sourceId} size="xs" />
              ))}
            </div>
          ) : (
            <button
              onClick={() => navigate('/app/integrations')}
              className="text-[10px] font-medium text-amber-600 hover:text-amber-700 hover:underline"
              data-testid="kpi-no-source-connect"
            >
              No live source connected — connect to see your real numbers
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {kpis.map(kpi => (
            <KPICard 
              key={kpi.id} 
              kpi={kpi} 
              onClick={() => navigate('/app/management')}
              showBenchmark={true}
              industry={industry}
            />
          ))}
        </div>
        
        {/* Benchmark Source Attribution */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <Building2 className="w-3 h-3" />
            <span>Industry benchmarks from ATO, IBISWorld, and industry associations</span>
          </div>
          <Button variant="link" size="sm" className="text-[10px] h-auto p-0 text-indigo-600">
            View all sources <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Live Metrics Bar
 */
export const LiveMetricsBar = ({ liveMetrics }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="glass-card overflow-hidden cursor-pointer hover:shadow-lg transition-all"
      onClick={() => navigate('/app/live-operations')}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-green-500" />
            Live Metrics
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-xs h-7">
            View Live <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
 <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-slate-200">
          {liveMetrics.map((metric, idx) => (
            <div key={`${metric?.id ?? metric?.name ?? metric?.title ?? 'metric'}-${idx}`} className="p-4 text-center hover:bg-primary/5 transition-colors">
              <p className="text-2xl sm:text-3xl font-display font-bold text-primary">
                {metric.value}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{metric.name}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Tasks & Recommendations Two-Column Layout
 */
export const TasksRecommendationsRow = ({ tasks, recommendations, onToggleTask, onExpandTask, setActiveTab }) => {
  return (
 <div className="grid gap-4 sm:gap-6">
      {/* Priority Tasks */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <ListTodo className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Priority Tasks
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('tasks')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {tasks.filter(t => t.status !== 'completed').slice(0, 4).map(task => (
            <EnhancedTaskItem 
              key={task.id} 
              task={task} 
              onToggle={onToggleTask} 
              onExpand={onExpandTask}
              compact 
            />
          ))}
          {tasks.filter(t => t.status !== 'completed').length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="text-sm">All caught up!</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* AI Recommendations */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              AI Recommendations
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('insights')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {recommendations.slice(0, 2).map((rec, idx) => (
            <RecommendationCard key={`${rec?.id ?? rec?.name ?? rec?.title ?? 'rec'}-${idx}`} rec={rec} compact />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Desktop Quick Stats Grid
 */
export const QuickStatsGrid = ({ completedCount, pendingCount, highPriorityCount, kpis, setActiveTab, setFilter }) => {
  const navigate = useNavigate();
  
  return (
 <div className="hidden sm:grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card 
        className="glass-card cursor-pointer hover:shadow-lg transition-all group"
        onClick={() => setActiveTab('tasks')}
      >
        <CardContent className="p-4 text-center">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500 group-hover:scale-110 transition-transform" />
          <p className="text-2xl font-display font-bold">{completedCount}</p>
          <p className="text-xs text-muted-foreground">Tasks Completed</p>
        </CardContent>
      </Card>
      <Card 
        className="glass-card cursor-pointer hover:shadow-lg transition-all group"
        onClick={() => setActiveTab('tasks')}
      >
        <CardContent className="p-4 text-center">
          <Clock className="w-6 h-6 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
          <p className="text-2xl font-display font-bold">{pendingCount}</p>
          <p className="text-xs text-muted-foreground">Tasks Pending</p>
        </CardContent>
      </Card>
      <Card 
        className="glass-card cursor-pointer hover:shadow-lg transition-all group"
        onClick={() => { setActiveTab('tasks'); setFilter('high'); }}
      >
        <CardContent className="p-4 text-center">
          <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-500 group-hover:scale-110 transition-transform" />
          <p className="text-2xl font-display font-bold">{highPriorityCount}</p>
          <p className="text-xs text-muted-foreground">High Priority</p>
        </CardContent>
      </Card>
      <Card 
        className="glass-card cursor-pointer hover:shadow-lg transition-all group"
        onClick={() => navigate('/app/management')}
      >
        <CardContent className="p-4 text-center">
          <Target className="w-6 h-6 mx-auto mb-2 text-blue-500 group-hover:scale-110 transition-transform" />
          <p className="text-2xl font-display font-bold">{kpis.filter(k => k.trend >= 0).length}/{kpis.length}</p>
          <p className="text-xs text-muted-foreground">KPIs On Track</p>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Quick Links & Settings Section — Industry-aware
 */
export const QuickLinksSection = ({ industry }) => {
  const navigate = useNavigate();
  const isQSR = industry === 'qsr';
  
  const links = isQSR ? [
    { label: 'Operations', icon: Activity, path: '/app/live-operations', color: 'text-green-500', testId: 'quick-link-operations' },
    { label: 'SPLH', icon: BarChart3, path: '/app/management', color: 'text-blue-500', testId: 'quick-link-splh' },
    { label: 'Compliance', icon: Shield, path: '/app/compliance', color: 'text-amber-500', testId: 'quick-link-compliance' },
    { label: 'Financials', icon: TrendingUp, path: '/app/financials', color: 'text-emerald-500', testId: 'quick-link-financials' },
  ] : [
    { label: 'Analytics', icon: LineChart, path: '/app/management', color: 'text-blue-500', testId: 'quick-link-analytics' },
    { label: 'Operations', icon: Activity, path: '/app/live-operations', color: 'text-green-500', testId: 'quick-link-operations' },
    { label: 'Financials', icon: TrendingUp, path: '/app/financials', color: 'text-emerald-500', testId: 'quick-link-financials' },
    { label: 'CRM', icon: MessageSquare, path: '/corvana-crm', color: 'text-violet-500', testId: 'quick-link-crm' },
  ];
  
  return (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            Quick Links
          </CardTitle>
        </CardHeader>
        <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {links.map(link => (
              <Button 
                key={link.testId}
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/5"
                onClick={() => navigate(link.path)}
                data-testid={link.testId}
              >
                <link.icon className={`w-4 h-4 ${link.color}`} />
                <span className="text-xs">{link.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Quick Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/5"
              onClick={() => navigate('/app/integrations')}
              data-testid="quick-settings-integrations"
            >
              <Package className="w-4 h-4 text-[#007AFF]" />
              <span className="text-xs">Integrations</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/5"
              onClick={() => navigate('/team')}
              data-testid="quick-settings-team"
            >
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-xs">Team</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/5"
              onClick={() => navigate('/app/business-settings')}
              data-testid="quick-settings-business"
            >
              <Building2 className="w-4 h-4 text-purple-500" />
              <span className="text-xs">Business</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/5"
              onClick={() => navigate('/app/profile')}
              data-testid="quick-settings-profile"
            >
              <UserCircle className="w-4 h-4 text-amber-500" />
              <span className="text-xs">Profile</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Recent Activity Section — Industry-aware
 */
export const RecentActivitySection = ({ industry, currentLocation, isDemoAccount = false }) => {
  const isQSR = industry === 'qsr';
  const locName = currentLocation?.shortName || 'All Locations';

  const qsrActivities = currentLocation ? [
    { text: `${locName}: Till reconciliation completed`, time: '12 min ago', icon: CheckCircle2, color: 'text-emerald-500' },
    { text: `${locName}: Temp log recorded — all zones compliant`, time: '28 min ago', icon: Shield, color: 'text-blue-500' },
    { text: `${locName}: Shift changeover handoff submitted`, time: '1 hr ago', icon: Users, color: 'text-purple-500' },
    { text: `${locName}: Drive-through peak throughput 118 cars/hr`, time: '2 hr ago', icon: Activity, color: 'text-amber-500' },
  ] : [
    { text: 'Southport: SPLH hit $71.20 — network high', time: '15 min ago', icon: Award, color: 'text-emerald-500' },
    { text: 'Footscray: Temp log gap flagged — Zone B', time: '45 min ago', icon: AlertTriangle, color: 'text-amber-500' },
    { text: 'Penrith: Drive-through accuracy at 98.5%', time: '1 hr ago', icon: Target, color: 'text-blue-500' },
    { text: 'Network: Weekly SPLH report generated', time: '3 hr ago', icon: BarChart3, color: 'text-purple-500' },
  ];

  const defaultActivities = [
    { text: 'Revenue forecast updated from Xero data', time: '20 min ago', icon: TrendingUp, color: 'text-emerald-500' },
    { text: 'New customer review received (4.8 stars)', time: '1 hr ago', icon: MessageSquare, color: 'text-blue-500' },
    { text: 'Staff roster for next week approved', time: '2 hr ago', icon: Users, color: 'text-purple-500' },
    { text: 'Monthly benchmark report ready', time: '4 hr ago', icon: BarChart3, color: 'text-amber-500' },
  ];

  const activities = isQSR ? qsrActivities : defaultActivities;

  // Live (non-demo) accounts must never see canned demo activity ("updated from
  // Xero data", fake reviews, etc.). Show a clean empty state until a real
  // activity feed is wired for this tenant.
  if (!isDemoAccount) {
    return (
      <Card className="glass-card" data-testid="recent-activity-empty">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">Live activity appears here as your team works and your connected sources sync — no placeholder events.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          Recent Activity
          {isQSR && currentLocation && (
            <Badge variant="outline" className="text-[10px] ml-1">{locName}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {activities.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={`${item?.id ?? item?.name ?? item?.title ?? 'item'}-${idx}`} className="flex items-center gap-3">
              <Icon className={`w-4 h-4 flex-shrink-0 ${item.color}`} />
              <span className="text-sm text-slate-700 flex-1">{item.text}</span>
              <span className="text-xs text-slate-400 whitespace-nowrap">{item.time}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
